import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Communication } from "../../types/Communication";
import CommunicationList from "../CommunicationList/CommunicationList";
import CommunicationCreateForm from "../CommunicationCreateForm/CommunicationCreateForm";
import CommunicationEditForm from "../CommunicationEditForm/CommunicationEditForm";
import { deleteCommunication, fetchCommunications, saveCommunication } from "../../utils/fetcher";
import { CIconButton } from "@carrefour/design-system-components-react";

interface PopupProps {
  open: boolean;
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1300;
  overflow: hidden;
`;

const ModalContainer = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  min-height: 650px;
  max-height: 90vh;
  overflow-y: visible;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 769px) {
    width: 800px;
    min-height: 700px;
  }
`;

const IconButtonCustom = styled(CIconButton)<{ active?: boolean }>`
  color: ${(props) => (props.active ? "#0970e6" : "")};
  position: absolute;
  top: 8px;
  right: 8px;
`;

const CommunicationModal: React.FC<PopupProps> = ({ open, onClose }) => {
  
  const [communicationsArray, setCommunicationsArray] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewToShow, setViewToShow] = useState("list");
  const [currentComm, setCurrentComm] = useState<Communication | null>(null);

  useEffect(() => {
    if (open) {
      setViewToShow("list");
      document.body.style.overflow = 'hidden';
      setLoading(true);

      fetchCommunications()
        .then((data) => {
          setCommunicationsArray(data || []);
        })
        .catch((err) => {
          console.error(err);
          setCommunicationsArray([]);
        })
        .finally(() => setLoading(false));
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleSave = (comm: Communication) => {
    saveCommunication(comm)
      .then((newComm) => {
        if (newComm) {
          setCommunicationsArray((prev) => {
            const index = prev.findIndex((c) => c.id === newComm.id);
            if (index !== -1) {
              const updated = [...prev];
              updated[index] = newComm;
              return updated;
            }
            return [...prev, newComm];
          });
          setCurrentComm(newComm);
          setViewToShow("list");
        }
      })
      .catch((err) => console.error('>error:',err));
  };

  const showDetailCommunication = (comm: Communication) => {
    setCurrentComm(comm);
    setViewToShow("edit-detail");
  };
  
  const handleDeleteComm = (id: string) => {
    deleteCommunication(id).then(() => {
      setLoading(true);
      setViewToShow("list");
      fetchCommunications()
        .then((data) => {
          setCommunicationsArray(data || []);
        })
        .catch((err) => {
          console.error(err);
          setCommunicationsArray([]);
        })
        .finally(() => setLoading(false));
    }).catch((err) => console.error(err));
  };

  if (!open) return null;

  return (
    <Overlay role="dialog" aria-modal="true">
      <ModalContainer>
        <IconButtonCustom
          aria-label="Close popup"
          icon="close"
          tone="loyalty"
          variation={"transparent"}
          size={"m"}
          active={true}
          onClick={onClose}
        />

        {viewToShow === "list" && (
          <CommunicationList
            communications={communicationsArray}
            loading={loading}
            showDetail={showDetailCommunication}
            onNew={() => {
              setCurrentComm(null);
              setViewToShow("create");
            }}
            onClose={onClose}
          />
        )}
        {viewToShow === "create" && (
          <CommunicationCreateForm
            initialData={currentComm} 
            onSave={(data) => {
              handleSave(data);
            }}
            onCancel={() => setViewToShow("list")} 
          />
        )}
        {viewToShow === "edit-detail" && (
          <CommunicationEditForm
            initialData={currentComm} 
            onSave={(data) => {
              handleSave(data);
            }}
            onCancel={() => setViewToShow("list")} 
            onDelete={handleDeleteComm}
          />
        )}
      </ModalContainer>
    </Overlay>
  );
}

export default CommunicationModal;
